import { useState } from 'react';
import { View, Text, ActivityIndicator, Modal, ScrollView, Pressable, TextInput } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuthStore } from '../../../../../../store/authStore';
import { useStyles } from '../../../../../../hooks/useStyles';
import { useResponsive } from '../../../../../../shared/utils/responsive';
import { usePropertyData } from '../../../../../../hooks/usePropertyData';
import { addColorOpacity } from '../../../../../../shared/utils/styles';
import { getDisplayName } from '../../../../../../shared/utils/user';
import { PropertyDetail, PropertyPartner } from '../../../../../../shared/types/property';
import { FinancialEntry, FinancialEntryType } from '../../../../../../shared/types/financial';
import { PortfolioPartner } from '../../../../../../shared/types/portfolio';
import { AddressSearchResult } from '../../../../../../shared/utils/api';
import { PropertyOverview, PropertyOwnership, PropertyFinancials, PropertyContacts, ParcelsTab } from '../../../../../../components/property';
import { SurveyModalHeader, SurveyItemInput, SurveyItemList, SurveyEmptyState, SurveyChecklist } from '../../../../../../components/survey';
import { CommentSection } from '../../../../../../components/comments';
import { DueDiligenceView } from '../../../../../../components/due-diligence';
import { AddressSearch } from '../../../../../../components/map';
import { Card, Button, Input, BackButton, TabBar } from '../../../../../../components/ui';
import { PageContainer } from '../../../../../../components/layout';
import { Ionicons } from '@expo/vector-icons';

// Human-friendly labels for property statuses
const STATUS_LABELS: Record<PropertyDetail['status'], string> = {
  research: 'Looking At',
  under_contract: 'Buying',
  owned: 'Owned',
  listed_for_sale: 'Selling',
  sold: 'Sold',
};

type TabKey = 'overview' | 'partners' | 'financials' | 'contacts' | 'parcels' | 'due_diligence' | 'discussion';

export default function PropertyDetailPage() {
  const { id, propertyId, threadId } = useLocalSearchParams<{ id: string; propertyId: string; threadId?: string }>();
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const styles = useStyles();
  const { isMobile, isSmallMobile } = useResponsive();

  // Use the custom hook for all property data and operations
  const {
    property,
    loading,
    portfolioPartners,
    financialEntries,
    loadingEntries,
    surveys,
    loadingSurveys,
    availablePartners,
    totalOwnership,
    grossIncome,
    totalExpenses,
    netCashFlow,
    updateProperty,
    deleteProperty,
    addPartner,
    updatePartnerOwnership,
    removePartner,
    addFinancialEntry,
    updateFinancialEntry,
    deleteFinancialEntry,
    createSurvey,
    addSurveyItem,
    toggleSurveyVote,
    deleteSurveyItem,
  } = usePropertyData({ 
    portfolioId: id as string, 
    propertyId: propertyId as string 
  });

  // Tab state
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Modal states
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addressSearchKey, setAddressSearchKey] = useState(0); // Key to reset AddressSearch
  const [addPartnerModalVisible, setAddPartnerModalVisible] = useState(false);
  const [editOwnershipModalVisible, setEditOwnershipModalVisible] = useState(false);
  const [addEntryModalVisible, setAddEntryModalVisible] = useState(false);
  const [editEntryModalVisible, setEditEntryModalVisible] = useState(false);

  // Edit property form
  const [editStreetAddress, setEditStreetAddress] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editZipCode, setEditZipCode] = useState('');
  const [editCounty, setEditCounty] = useState('');
  const [editLatitude, setEditLatitude] = useState('');
  const [editLongitude, setEditLongitude] = useState('');
  const [editAcreage, setEditAcreage] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editZillowUrl, setEditZillowUrl] = useState('');
  const [editStatus, setEditStatus] = useState<PropertyDetail['status']>('research');
  const [editing, setEditing] = useState(false);

  // Add partner form
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const [partnerOwnership, setPartnerOwnership] = useState('');
  const [addingPartner, setAddingPartner] = useState(false);

  // Edit ownership form
  const [editingPartner, setEditingPartner] = useState<PropertyPartner | null>(null);
  const [editOwnershipPercentage, setEditOwnershipPercentage] = useState('');
  const [updatingOwnership, setUpdatingOwnership] = useState(false);

  // Financial entry form
  const [entryType, setEntryType] = useState<FinancialEntryType>('income');
  const [entryAmount, setEntryAmount] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryDescription, setEntryDescription] = useState('');
  const [entryCategory, setEntryCategory] = useState('');
  const [addingEntry, setAddingEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FinancialEntry | null>(null);
  const [updatingEntry, setUpdatingEntry] = useState(false);

  // Survey state
  const [selectedSurvey, setSelectedSurvey] = useState<any>(null);
  const [newSurveyItemText, setNewSurveyItemText] = useState('');
  const [addingSurveyItem, setAddingSurveyItem] = useState(false);
  const [creatingSurvey, setCreatingSurvey] = useState(false);

  // Open edit modal
  const openEditModal = () => {
    if (!property) return;
    setEditStreetAddress(property.street_address || property.address || '');
    setEditCity(property.city || '');
    setEditState(property.state || '');
    setEditZipCode(property.zip_code || '');
    setEditCounty(property.county || '');
    setEditLatitude(property.latitude?.toString() || '');
    setEditLongitude(property.longitude?.toString() || '');
    setEditAcreage(property.acreage?.toString() || '');
    setEditValue(property.property_value?.toString() || '');
    setEditZillowUrl(property.zillow_url || '');
    setEditStatus(property.status);
    setEditModalVisible(true);
  };

  // Handle update property
  const handleUpdateProperty = async () => {
    if (!editStreetAddress.trim()) {
      return;
    }
    const propertyValue = editValue.trim() ? parseFloat(editValue) : null;
    if (propertyValue !== null && (isNaN(propertyValue) || propertyValue < 0)) {
      return;
    }
    
    // Parse coordinates
    const latitude = editLatitude.trim() ? parseFloat(editLatitude) : null;
    const longitude = editLongitude.trim() ? parseFloat(editLongitude) : null;
    const acreage = editAcreage.trim() ? parseFloat(editAcreage) : null;
    
    // Validate coordinates
    if (latitude !== null && (isNaN(latitude) || latitude < -90 || latitude > 90)) {
      return;
    }
    if (longitude !== null && (isNaN(longitude) || longitude < -180 || longitude > 180)) {
      return;
    }
    if (acreage !== null && (isNaN(acreage) || acreage < 0)) {
      return;
    }

    setEditing(true);
    const success = await updateProperty({
      street_address: editStreetAddress.trim() || undefined,
      city: editCity.trim() || undefined,
      state: editState.trim() || undefined,
      zip_code: editZipCode.trim() || undefined,
      county: editCounty.trim() || undefined,
      latitude,
      longitude,
      acreage,
      property_value: propertyValue,
      zillow_url: editZillowUrl.trim() || null,
      status: editStatus,
    });
    setEditing(false);
    if (success) {
      setEditModalVisible(false);
    }
  };

  // Handle add partner
  const handleAddPartner = async () => {
    if (!selectedPartnerId || !partnerOwnership.trim()) return;
    const ownership = parseFloat(partnerOwnership);
    if (isNaN(ownership) || ownership <= 0 || ownership > 100) return;

    setAddingPartner(true);
    const success = await addPartner(selectedPartnerId, ownership);
    setAddingPartner(false);
    if (success) {
      setAddPartnerModalVisible(false);
      setSelectedPartnerId(null);
      setPartnerOwnership('');
    }
  };

  // Handle update ownership
  const handleUpdateOwnership = async () => {
    if (!editingPartner || !editOwnershipPercentage.trim()) return;
    const ownership = parseFloat(editOwnershipPercentage);
    if (isNaN(ownership) || ownership <= 0 || ownership > 100) return;

    setUpdatingOwnership(true);
    const success = await updatePartnerOwnership(editingPartner.user_id, ownership);
    setUpdatingOwnership(false);
    if (success) {
      setEditOwnershipModalVisible(false);
      setEditingPartner(null);
      setEditOwnershipPercentage('');
    }
  };

  // Open edit ownership modal
  const openEditOwnershipModal = (partner: PropertyPartner) => {
    setEditingPartner(partner);
    setEditOwnershipPercentage(partner.ownership_percentage.toString());
    setEditOwnershipModalVisible(true);
  };

  // Handle add entry
  const handleAddEntry = async () => {
    if (!entryAmount.trim()) return;
    const amount = parseFloat(entryAmount);
    if (isNaN(amount) || amount <= 0) return;

    setAddingEntry(true);
    const success = await addFinancialEntry({
      type: entryType,
      amount,
      date: entryDate,
      description: entryDescription.trim() || null,
      category: entryCategory.trim() || null,
    });
    setAddingEntry(false);
    if (success) {
      setAddEntryModalVisible(false);
      resetEntryForm();
    }
  };

  // Reset entry form
  const resetEntryForm = () => {
    setEntryType('income');
    setEntryAmount('');
    setEntryDate(new Date().toISOString().split('T')[0]);
    setEntryDescription('');
    setEntryCategory('');
  };

  // Open edit entry modal
  const openEditEntryModal = (entry: FinancialEntry) => {
    setEditingEntry(entry);
    setEntryType(entry.type);
    setEntryAmount(entry.amount.toString());
    setEntryDate(entry.date.split('T')[0]);
    setEntryDescription(entry.description || '');
    setEntryCategory(entry.category || '');
    setEditEntryModalVisible(true);
  };

  // Handle update entry
  const handleUpdateEntry = async () => {
    if (!editingEntry || !entryAmount.trim()) return;
    const amount = parseFloat(entryAmount);
    if (isNaN(amount) || amount <= 0) return;

    setUpdatingEntry(true);
    const success = await updateFinancialEntry(editingEntry.id, {
      type: entryType,
      amount,
      date: entryDate,
      description: entryDescription.trim() || null,
      category: entryCategory.trim() || null,
    });
    setUpdatingEntry(false);
    if (success) {
      setEditEntryModalVisible(false);
      setEditingEntry(null);
      resetEntryForm();
    }
  };

  // Handle delete entry
  const handleDeleteEntry = async (entry: FinancialEntry) => {
    await deleteFinancialEntry(entry.id);
  };

  // Survey handlers
  const handleCreateSurvey = async (phase: 'onset' | 'intermediate' | 'final') => {
    setCreatingSurvey(true);
    const survey = await createSurvey(phase);
    setCreatingSurvey(false);
    if (survey) {
      setSelectedSurvey(survey);
    }
  };

  const handleAddSurveyItem = async () => {
    if (!selectedSurvey || !newSurveyItemText.trim()) return;
    setAddingSurveyItem(true);
    await addSurveyItem(selectedSurvey.id, newSurveyItemText.trim());
    setAddingSurveyItem(false);
    setNewSurveyItemText('');
  };

  const handleToggleVote = async (surveyId: number, itemId: number) => {
    await toggleSurveyVote(surveyId, itemId);
  };

  const handleDeleteSurveyItem = async (surveyId: number, itemId: number) => {
    await deleteSurveyItem(surveyId, itemId);
  };

  // Build tabs with badges to hint at interaction/content
  const propertyPartners = property?.partners || [];
  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'home-outline' as const },
    { key: 'partners', label: 'Partners', icon: 'people-outline' as const, badge: propertyPartners.length || undefined },
    { key: 'financials', label: 'Financials', icon: 'calculator-outline' as const, badge: financialEntries.length || undefined },
    { key: 'contacts', label: 'Contacts', icon: 'call-outline' as const },
    { key: 'parcels', label: 'Parcels', icon: 'map-outline' as const },
    ...(property?.status === 'research' ? [{ key: 'due_diligence', label: 'Due Diligence', icon: 'checkbox-outline' as const }] : []),
    { key: 'discussion', label: 'Discussion', icon: 'chatbubbles-outline' as const },
  ];

  // Loading state
  if (loading) {
    return (
      <PageContainer>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="large" color={styles.tokens.color.primary} />
          <Text style={[styles.text('base'), { marginTop: styles.utils.spacing.md, color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
            Loading property...
          </Text>
        </View>
      </PageContainer>
    );
  }

  // No property found
  if (!property) {
    return (
      <PageContainer>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={[styles.text('lg'), { color: styles.tokens.color.error }]}>Property not found</Text>
          <Button 
            title="Go Back" 
            onPress={() => router.push(`/portfolios/${id}`)} 
            variant="primary"
            style={{ marginTop: styles.utils.spacing.lg }}
          />
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <View style={[styles.row, { 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: styles.utils.spacing.lg,
        flexDirection: isMobile ? 'column' : 'row',
        gap: styles.utils.spacing.md,
      }]}>
        <BackButton fallbackRoute={`/portfolios/${id}`} />
        
        <View style={[styles.row, { 
          gap: styles.utils.spacing.sm,
          width: isMobile ? '100%' : undefined,
        }]}>
          <Button 
            title={isSmallMobile ? "Edit" : "Edit Property"}
            onPress={openEditModal} 
            variant="secondary"
            size={isSmallMobile ? "sm" : undefined}
            style={isMobile ? { flex: 1 } : undefined}
          />
          <Button 
            title={isSmallMobile ? "Delete" : "Delete Property"}
            onPress={deleteProperty} 
            variant="danger"
            size={isSmallMobile ? "sm" : undefined}
            style={isMobile ? { flex: 1 } : undefined}
          />
        </View>
      </View>

      {/* Tab Navigation */}
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key: string) => setActiveTab(key as TabKey)}
      />

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <PropertyOverview
          property={property}
          currentUserId={user?.id}
          grossIncome={grossIncome}
          totalExpenses={totalExpenses}
          netCashFlow={netCashFlow}
        />
      )}

      {activeTab === 'partners' && (
        <PropertyOwnership
          partners={property.partners}
          totalOwnership={totalOwnership}
          currentUserId={user?.id}
          onAddPartner={() => setAddPartnerModalVisible(true)}
          onEditOwnership={openEditOwnershipModal}
          onRemovePartner={removePartner}
        />
      )}

      {activeTab === 'financials' && (
        <PropertyFinancials
          portfolioId={id as string}
          propertyId={propertyId as string}
          entries={financialEntries}
          loading={loadingEntries}
          onAddEntry={() => {
            resetEntryForm();
            setAddEntryModalVisible(true);
          }}
          onEditEntry={openEditEntryModal}
          onDeleteEntry={handleDeleteEntry}
        />
      )}

      {activeTab === 'contacts' && (
        <PropertyContacts
          portfolioId={parseInt(id, 10)}
          propertyId={parseInt(propertyId, 10)}
        />
      )}

      {activeTab === 'parcels' && (
        <ParcelsTab
          property={property}
          portfolioId={parseInt(id, 10)}
        />
      )}

      {activeTab === 'due_diligence' && property.status === 'research' && (
        <DueDiligenceView
          portfolioId={parseInt(id, 10)}
          propertyId={parseInt(propertyId, 10)}
          availablePartners={availablePartners.map(p => ({
            id: p.user_id,
            email: p.email,
            display_name: undefined,
          }))}
        />
      )}

      {activeTab === 'discussion' && (
        <CommentSection
          contextType="property"
          contextId={property.id}
          title="Discussion"
          autoCreateThread={true}
          defaultThreadTitle="Property Discussion"
          threadId={threadId ? parseInt(threadId, 10) : null}
        />
      )}

      {/* Edit Property Modal */}
      <Modal
        visible={editModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setEditModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setEditModalVisible(false)}>
          <Pressable 
            style={{
              backgroundColor: styles.tokens.color.background,
              borderRadius: styles.utils.radius.lg,
              padding: styles.utils.spacing.xl,
              width: isMobile ? '90%' : 550,
              maxHeight: '85%',
            }} 
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={[styles.heading('xl'), { marginBottom: styles.utils.spacing.lg }]}>
              Edit Property
            </Text>
            
            <ScrollView style={{ maxHeight: isMobile ? 400 : 500 }} keyboardShouldPersistTaps="handled">
              {/* Quick Address Lookup - PROMINENT FEATURE */}
              <View style={{ 
                backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.12), 
                padding: styles.utils.spacing.lg, 
                borderRadius: styles.utils.radius.lg,
                marginBottom: styles.utils.spacing.xl,
                borderWidth: 2,
                borderColor: addColorOpacity(styles.tokens.color.primary, 0.3),
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: styles.utils.spacing.sm }}>
                  <View style={{
                    backgroundColor: styles.tokens.color.primary,
                    borderRadius: styles.utils.radius.full,
                    padding: styles.utils.spacing.xs,
                    marginRight: styles.utils.spacing.sm,
                  }}>
                    <Ionicons name="search" size={16} color={styles.tokens.color.onPrimary} />
                  </View>
                  <Text style={[styles.text('base'), { color: styles.tokens.color.primary, fontWeight: styles.utils.typography.weights.bold }]}>
                    Quick Address Lookup
                  </Text>
                </View>
                <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onSurface, 0.7), marginBottom: styles.utils.spacing.md }]}>
                  Paste an address from Zillow, Google Maps, or type to search. All fields will auto-fill!
                </Text>
                <AddressSearch
                  key={addressSearchKey}
                  placeholder="Paste or type address here..."
                  onSelect={(result: AddressSearchResult) => {
                    // Auto-fill all fields from geocoding result
                    setEditStreetAddress(result.address?.street_address || result.formatted_address.split(',')[0] || '');
                    setEditCity(result.address?.city || '');
                    setEditState(result.address?.state_abbr || result.address?.state || '');
                    setEditZipCode(result.address?.zip_code || '');
                    setEditCounty(result.address?.county || '');
                    if (result.coordinates?.latitude) {
                      setEditLatitude(String(result.coordinates.latitude));
                    }
                    if (result.coordinates?.longitude) {
                      setEditLongitude(String(result.coordinates.longitude));
                    }
                    // Reset the search key to clear the input after selection
                    setAddressSearchKey(prev => prev + 1);
                  }}
                />
              </View>

              {/* Address Section */}
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold, color: styles.tokens.color.primary, marginBottom: styles.utils.spacing.sm }]}>
                Address
              </Text>
              <Input
                label="Street Address *"
                placeholder="123 Main Street"
                value={editStreetAddress}
                onChangeText={setEditStreetAddress}
                autoCapitalize="words"
              />

              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.md, marginBottom: styles.utils.spacing.md }}>
                <View style={{ flex: 2 }}>
                  <Input label="City" placeholder="City" value={editCity} onChangeText={setEditCity} />
                </View>
                <View style={{ flex: 1 }}>
                  <Input label="State" placeholder="ST" value={editState} onChangeText={setEditState} maxLength={2} />
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.md, marginBottom: styles.utils.spacing.md }}>
                <View style={{ flex: 1 }}>
                  <Input label="ZIP Code" placeholder="12345" value={editZipCode} onChangeText={setEditZipCode} keyboardType="numeric" />
                </View>
                <View style={{ flex: 1 }}>
                  <Input label="County" placeholder="Travis County" value={editCounty} onChangeText={setEditCounty} />
                </View>
              </View>

              {/* Location Section */}
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold, color: styles.tokens.color.primary, marginTop: styles.utils.spacing.md, marginBottom: styles.utils.spacing.sm }]}>
                Location & Size
              </Text>
              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.md, marginBottom: styles.utils.spacing.md }}>
                <View style={{ flex: 1 }}>
                  <Input 
                    label="Latitude" 
                    placeholder="30.2672" 
                    value={editLatitude} 
                    onChangeText={setEditLatitude} 
                    keyboardType="decimal-pad"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Input 
                    label="Longitude" 
                    placeholder="-97.7431" 
                    value={editLongitude} 
                    onChangeText={setEditLongitude} 
                    keyboardType="decimal-pad"
                  />
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.md, marginBottom: styles.utils.spacing.md }}>
                <View style={{ flex: 1 }}>
                  <Input 
                    label="Acreage" 
                    placeholder="10.5" 
                    value={editAcreage} 
                    onChangeText={setEditAcreage} 
                    keyboardType="decimal-pad"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Input label="Property Value" placeholder="500000" value={editValue} onChangeText={setEditValue} keyboardType="numeric" />
                </View>
              </View>

              {/* Links Section */}
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold, color: styles.tokens.color.primary, marginTop: styles.utils.spacing.md, marginBottom: styles.utils.spacing.sm }]}>
                Links
              </Text>
              <Input label="Zillow URL" placeholder="https://zillow.com/..." value={editZillowUrl} onChangeText={setEditZillowUrl} />

              <View style={{ marginBottom: styles.utils.spacing.md }}>
                <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold, marginBottom: styles.utils.spacing.sm }]}>
                  Status:
                </Text>
                {(['research', 'under_contract', 'owned', 'listed_for_sale', 'sold'] as PropertyDetail['status'][]).map((status) => (
                  <Pressable
                    key={status}
                    onPress={() => setEditStatus(status)}
                    style={{
                      paddingHorizontal: styles.utils.spacing.md,
                      paddingVertical: styles.utils.spacing.sm,
                      borderRadius: styles.utils.radius.md,
                      backgroundColor: editStatus === status ? styles.tokens.color.primary : styles.tokens.color.surface,
                      borderWidth: 1,
                      borderColor: editStatus === status ? styles.tokens.color.primary : styles.tokens.color.border,
                      marginBottom: styles.utils.spacing.xs,
                    }}
                  >
                    <Text style={[styles.text('sm'), { 
                      color: editStatus === status ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface,
                      fontWeight: editStatus === status ? styles.utils.typography.weights.semibold : styles.utils.typography.weights.regular,
                    }]}>
                      {STATUS_LABELS[status]}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </ScrollView>

            <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: styles.utils.spacing.lg }]}>
              <Button title="Cancel" onPress={() => setEditModalVisible(false)} variant="outline" />
              <Button 
                title={editing ? 'Updating...' : 'Update'} 
                onPress={handleUpdateProperty} 
                disabled={editing || !editStreetAddress.trim()}
                variant="primary"
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Add Partner Modal */}
      <Modal
        visible={addPartnerModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setAddPartnerModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setAddPartnerModalVisible(false)}>
          <Pressable 
            style={{
              backgroundColor: styles.tokens.color.background,
              borderRadius: styles.utils.radius.lg,
              padding: styles.utils.spacing.xl,
              width: isMobile ? '90%' : 400,
            }} 
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={[styles.heading('xl'), { marginBottom: styles.utils.spacing.lg }]}>
              Add Partner
            </Text>

            <Text style={[styles.text('base'), { marginBottom: styles.utils.spacing.sm }]}>
              Select Partner:
            </Text>
            <View style={{ marginBottom: styles.utils.spacing.md }}>
              {availablePartners.length === 0 ? (
                <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                  All portfolio partners are already added
                </Text>
              ) : (
                availablePartners.map((partner) => (
                  <Pressable
                    key={partner.user_id}
                    onPress={() => setSelectedPartnerId(partner.user_id)}
                    style={{
                      padding: styles.utils.spacing.md,
                      borderRadius: styles.utils.radius.md,
                      backgroundColor: selectedPartnerId === partner.user_id ? addColorOpacity(styles.tokens.color.primary, 0.1) : styles.tokens.color.surface,
                      borderWidth: 1,
                      borderColor: selectedPartnerId === partner.user_id ? styles.tokens.color.primary : styles.tokens.color.border,
                      marginBottom: styles.utils.spacing.xs,
                    }}
                  >
                    <Text style={[styles.text('base'), { 
                      color: selectedPartnerId === partner.user_id ? styles.tokens.color.primary : styles.tokens.color.onSurface,
                    }]}>
                      {getDisplayName(partner)}
                    </Text>
                  </Pressable>
                ))
              )}
            </View>

            <Input
              label="Ownership %"
              placeholder="25"
              value={partnerOwnership}
              onChangeText={setPartnerOwnership}
              keyboardType="numeric"
            />

            <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: styles.utils.spacing.lg }]}>
              <Button title="Cancel" onPress={() => setAddPartnerModalVisible(false)} variant="outline" />
              <Button 
                title={addingPartner ? 'Adding...' : 'Add Partner'} 
                onPress={handleAddPartner} 
                disabled={addingPartner || !selectedPartnerId || !partnerOwnership.trim()}
                variant="primary"
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Edit Ownership Modal */}
      <Modal
        visible={editOwnershipModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setEditOwnershipModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setEditOwnershipModalVisible(false)}>
          <Pressable 
            style={{
              backgroundColor: styles.tokens.color.background,
              borderRadius: styles.utils.radius.lg,
              padding: styles.utils.spacing.xl,
              width: isMobile ? '90%' : 400,
            }} 
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={[styles.heading('xl'), { marginBottom: styles.utils.spacing.lg }]}>
              Edit Ownership
            </Text>

            {editingPartner && (
              <Text style={[styles.text('base'), { marginBottom: styles.utils.spacing.md }]}>
                Partner: {getDisplayName(editingPartner)}
              </Text>
            )}

            <Input
              label="Ownership %"
              placeholder="25"
              value={editOwnershipPercentage}
              onChangeText={setEditOwnershipPercentage}
              keyboardType="numeric"
            />

            <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: styles.utils.spacing.lg }]}>
              <Button title="Cancel" onPress={() => setEditOwnershipModalVisible(false)} variant="outline" />
              <Button 
                title={updatingOwnership ? 'Updating...' : 'Update'} 
                onPress={handleUpdateOwnership} 
                disabled={updatingOwnership || !editOwnershipPercentage.trim()}
                variant="primary"
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Add Entry Modal */}
      <Modal
        visible={addEntryModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setAddEntryModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setAddEntryModalVisible(false)}>
          <Pressable 
            style={{
              backgroundColor: styles.tokens.color.background,
              borderRadius: styles.utils.radius.lg,
              padding: styles.utils.spacing.xl,
              width: isMobile ? '90%' : 450,
              maxHeight: '80%',
            }} 
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={[styles.heading('xl'), { marginBottom: styles.utils.spacing.lg }]}>
              Add Financial Entry
            </Text>
            
            <ScrollView>
              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.sm, marginBottom: styles.utils.spacing.md }}>
                <Pressable
                  onPress={() => setEntryType('income')}
                  style={{
                    flex: 1,
                    padding: styles.utils.spacing.md,
                    borderRadius: styles.utils.radius.md,
                    backgroundColor: entryType === 'income' ? addColorOpacity(styles.tokens.color.success, 0.2) : styles.tokens.color.surface,
                    borderWidth: 2,
                    borderColor: entryType === 'income' ? styles.tokens.color.success : styles.tokens.color.border,
                    alignItems: 'center',
                  }}
                >
                  <Ionicons name="arrow-down-circle" size={24} color={entryType === 'income' ? styles.tokens.color.success : styles.tokens.color.onSurface} />
                  <Text style={[styles.text('sm'), { 
                    color: entryType === 'income' ? styles.tokens.color.success : styles.tokens.color.onSurface,
                    fontWeight: styles.utils.typography.weights.semibold,
                    marginTop: styles.utils.spacing.xs,
                  }]}>
                    Income
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setEntryType('expense')}
                  style={{
                    flex: 1,
                    padding: styles.utils.spacing.md,
                    borderRadius: styles.utils.radius.md,
                    backgroundColor: entryType === 'expense' ? addColorOpacity(styles.tokens.color.error, 0.2) : styles.tokens.color.surface,
                    borderWidth: 2,
                    borderColor: entryType === 'expense' ? styles.tokens.color.error : styles.tokens.color.border,
                    alignItems: 'center',
                  }}
                >
                  <Ionicons name="arrow-up-circle" size={24} color={entryType === 'expense' ? styles.tokens.color.error : styles.tokens.color.onSurface} />
                  <Text style={[styles.text('sm'), { 
                    color: entryType === 'expense' ? styles.tokens.color.error : styles.tokens.color.onSurface,
                    fontWeight: styles.utils.typography.weights.semibold,
                    marginTop: styles.utils.spacing.xs,
                  }]}>
                    Expense
                  </Text>
                </Pressable>
              </View>

              <Input label="Amount *" placeholder="1000.00" value={entryAmount} onChangeText={setEntryAmount} keyboardType="numeric" />
              <Input label="Date" placeholder="YYYY-MM-DD" value={entryDate} onChangeText={setEntryDate} />
              <Input label="Description" placeholder="Monthly rent" value={entryDescription} onChangeText={setEntryDescription} />
              <Input label="Category" placeholder="Rent, Utilities, etc." value={entryCategory} onChangeText={setEntryCategory} />
            </ScrollView>

            <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: styles.utils.spacing.lg }]}>
              <Button title="Cancel" onPress={() => setAddEntryModalVisible(false)} variant="outline" />
              <Button 
                title={addingEntry ? 'Adding...' : 'Add Entry'} 
                onPress={handleAddEntry} 
                disabled={addingEntry || !entryAmount.trim()}
                variant="primary"
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Edit Entry Modal */}
      <Modal
        visible={editEntryModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setEditEntryModalVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setEditEntryModalVisible(false)}>
          <Pressable 
            style={{
              backgroundColor: styles.tokens.color.background,
              borderRadius: styles.utils.radius.lg,
              padding: styles.utils.spacing.xl,
              width: isMobile ? '90%' : 450,
              maxHeight: '80%',
            }} 
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={[styles.heading('xl'), { marginBottom: styles.utils.spacing.lg }]}>
              Edit Financial Entry
            </Text>
            
            <ScrollView>
              <View style={{ flexDirection: 'row', gap: styles.utils.spacing.sm, marginBottom: styles.utils.spacing.md }}>
                <Pressable
                  onPress={() => setEntryType('income')}
                  style={{
                    flex: 1,
                    padding: styles.utils.spacing.md,
                    borderRadius: styles.utils.radius.md,
                    backgroundColor: entryType === 'income' ? addColorOpacity(styles.tokens.color.success, 0.2) : styles.tokens.color.surface,
                    borderWidth: 2,
                    borderColor: entryType === 'income' ? styles.tokens.color.success : styles.tokens.color.border,
                    alignItems: 'center',
                  }}
                >
                  <Ionicons name="arrow-down-circle" size={24} color={entryType === 'income' ? styles.tokens.color.success : styles.tokens.color.onSurface} />
                  <Text style={[styles.text('sm'), { 
                    color: entryType === 'income' ? styles.tokens.color.success : styles.tokens.color.onSurface,
                    fontWeight: styles.utils.typography.weights.semibold,
                    marginTop: styles.utils.spacing.xs,
                  }]}>
                    Income
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setEntryType('expense')}
                  style={{
                    flex: 1,
                    padding: styles.utils.spacing.md,
                    borderRadius: styles.utils.radius.md,
                    backgroundColor: entryType === 'expense' ? addColorOpacity(styles.tokens.color.error, 0.2) : styles.tokens.color.surface,
                    borderWidth: 2,
                    borderColor: entryType === 'expense' ? styles.tokens.color.error : styles.tokens.color.border,
                    alignItems: 'center',
                  }}
                >
                  <Ionicons name="arrow-up-circle" size={24} color={entryType === 'expense' ? styles.tokens.color.error : styles.tokens.color.onSurface} />
                  <Text style={[styles.text('sm'), { 
                    color: entryType === 'expense' ? styles.tokens.color.error : styles.tokens.color.onSurface,
                    fontWeight: styles.utils.typography.weights.semibold,
                    marginTop: styles.utils.spacing.xs,
                  }]}>
                    Expense
                  </Text>
                </Pressable>
              </View>

              <Input label="Amount *" placeholder="1000.00" value={entryAmount} onChangeText={setEntryAmount} keyboardType="numeric" />
              <Input label="Date" placeholder="YYYY-MM-DD" value={entryDate} onChangeText={setEntryDate} />
              <Input label="Description" placeholder="Monthly rent" value={entryDescription} onChangeText={setEntryDescription} />
              <Input label="Category" placeholder="Rent, Utilities, etc." value={entryCategory} onChangeText={setEntryCategory} />
            </ScrollView>

            <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: styles.utils.spacing.lg }]}>
              <Button title="Cancel" onPress={() => setEditEntryModalVisible(false)} variant="outline" />
              <Button 
                title={updatingEntry ? 'Updating...' : 'Update Entry'} 
                onPress={handleUpdateEntry} 
                disabled={updatingEntry || !entryAmount.trim()}
                variant="primary"
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </PageContainer>
  );
}

